import {
  AccountContactPreferencesDocument,
  UpdateClientDetailsMutationVariables,
  useClientDetailsDataQuery,
  useUpdateClientDetailsMutation,
} from 'graphql/bindings/AdminBindings'
import {
  AdminField,
  CompanyField,
  OutreachTypeField,
  RelationshipSensitivityField,
  RelationshipStrengthField,
} from 'components/shared/dropdowns'
import {
  Box,
  FormModal,
  FormSectionTitle,
  HelperText,
  SpinnerButton,
  TextField,
  Typography,
} from '@forgeglobal/design-framework'
import {
  BusinessDevelopmentTierField,
  businessDevelopmentTierOpts,
} from 'components/shared/dropdowns/BusinessDevelopmentTierField'
import { DoNotContactFormProps } from 'util/DoNotContactUtils'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { MutationResult } from 'react-apollo'
import { OutreachOptions, outreachOptionsDropdown, relationshipStrengthOpts, sensitivityOpts } from 'util/Enums'
import { durationToOutreachOptions, getNextOutreachString } from 'components/dashboard/OutreachScheduleModal'
import HighlightedSection from 'components/shared/HighlightedSection'
import React, { Fragment, useEffect, useRef } from 'react'
import moment, { Moment } from 'moment'
import pick from 'lodash/pick'

interface FormShape {
  id: string
  owner?: { label: string; value: number }
  additional_owners: { label: string; value: number }[]
  relationship_strength?: { label: string; value: string }
  relationship_sensitivity?: { label: string; value: string }
  relationship_notes: string
  business_development_tier?: { label: string; value: number }
  has_influence_with: { label: string; value: number }[]
  mutationResult: MutationResult
  notes?: string
  days?: number
  outreachType: { label: string; value: OutreachOptions }
  lastContacted: Moment
}

const DetailsForm: React.FC<FormRenderProps<FormShape> & DoNotContactFormProps> = ({
  handleSubmit,
  values,
  editSection,
  highlightDNCFields,
}) => {
  const mutateInProgress = values.mutationResult.called
  const nextOutreachString = getNextOutreachString(values)

  const sensitivityRef = useRef(null)

  useEffect(() => {
    if (editSection === 'sensitivity') {
      ;(sensitivityRef.current as any).scrollIntoView()
    }
  })
  return (
    <form onSubmit={handleSubmit}>
      <FormSectionTitle>Coverage</FormSectionTitle>
      <AdminField name="owner" label="Owner" />
      <AdminField name="additional_owners" label="Additional Forge contact(s)" multiple />
      <RelationshipStrengthField name="relationship_strength" label="Relationship strength" />
      <HighlightedSection highlighted={highlightDNCFields || false} ref={sensitivityRef}>
        <RelationshipSensitivityField name="relationship_sensitivity" label="Relationship sensitivity" />
        {values.relationship_sensitivity?.value === 'high' && (
          <Fragment>
            <Field name="relationship_notes" label="Please describe sensitivity" component={TextField} fullWidth />
            <HelperText text="Accounts marked as 'High sensitivity' will be added to the 'Do Not Contact' list. Contact with this account will be recommended through the Forge owner only." />
          </Fragment>
        )}
      </HighlightedSection>
      <BusinessDevelopmentTierField name="business_development_tier" label="Business development tier" />

      <FormSectionTitle>Suggested Outreach Schedule</FormSectionTitle>
      <OutreachTypeField name="outreachType" label="Remind me if this account has not been contacted in" />
      {values.outreachType?.value === OutreachOptions.Custom && (
        <Field name="days" label="Number of days" component={TextField} fullWidth />
      )}
      <Box pt={1} pl={2}>
        <Typography variant="caption" color="textSecondary">
          {nextOutreachString}
        </Typography>
      </Box>

      <FormSectionTitle>Other Information</FormSectionTitle>
      <CompanyField name="has_influence_with" label="Influence with issuers" multiple />
      <Field name="notes" component={TextField} label="Additional information" fullWidth multiline />

      <Box mt={2}>
        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          loading={mutateInProgress}
          caption="Save"
        />
      </Box>
    </form>
  )
}

export const AccountDetailsModal: React.FC<{ hideModal: () => void; accountId: string } & DoNotContactFormProps> = ({
  hideModal,
  accountId,
  editSection,
  highlightDNCFields,
}) => {
  const { data } = useClientDetailsDataQuery({ variables: { id: accountId } })
  const [mutate, mutateResult] = useUpdateClientDetailsMutation({
    onCompleted: hideModal,
    refetchQueries: [{ query: AccountContactPreferencesDocument, variables: { id: accountId } }],
  })
  const account = data?.account
  const onSubmit = (values: FormShape) => {
    let schedule
    if (values.outreachType?.value === OutreachOptions.TwoWeeks) {
      schedule = moment.duration(14, 'days').toISOString()
    } else if (values.outreachType?.value === OutreachOptions.OneWeek) {
      schedule = moment.duration(7, 'days').toISOString()
    } else if (values.outreachType?.value === OutreachOptions.Never) {
      schedule = null
    } else {
      schedule = moment.duration(parseInt(`${values.days}` || '0'), 'days').toISOString()
    }
    const submitData: UpdateClientDetailsMutationVariables = {
      form: {
        ...pick(values, ['id', 'relationship_notes', 'notes']),
        has_influence_with: values.has_influence_with.map(h => h.value),
        outreach_schedule: schedule,
        relationship_strength: values.relationship_strength?.value,
        relationship_sensitivity: values.relationship_sensitivity?.value,
        business_development_tier: values.business_development_tier?.value,
        owners: [values.owner, ...values.additional_owners].filter(owner => !!owner).map(owner => owner!.value),
      },
    }
    mutate({ variables: submitData })
  }
  const [owner, ...additional_owners] = account?.owners ?? []
  const hasInfluenceWith = account?.has_influence_with ?? []

  const currentScheduleOpt = account?.outreach_schedule ?? undefined
  const currentSchedule = currentScheduleOpt ? moment.duration(currentScheduleOpt) : undefined
  const lastContactedOpt = account?.last_outreach_at
  const lastContacted = lastContactedOpt ? moment(lastContactedOpt) : moment()
  const outreachType = durationToOutreachOptions(currentSchedule)
  const outreachTypeOption = outreachOptionsDropdown.find(o => o.value === outreachType)
  const initialValues: FormShape = {
    id: account?.id ?? '',
    owner: owner && { label: owner.name, value: owner.owner_id },
    additional_owners: additional_owners.map(own => ({ label: own.name, value: own.owner_id })),
    relationship_strength: relationshipStrengthOpts.find(f => f.value === account?.relationship_strength),
    relationship_sensitivity: sensitivityOpts.find(f => f.value === account?.relationship_sensitivity),
    relationship_notes: account?.relationship_notes ?? '',
    business_development_tier: businessDevelopmentTierOpts.find(f => f.value === account?.business_development_tier),
    has_influence_with: hasInfluenceWith.map(comp => ({ label: comp.company_name, value: comp.company_id })),
    mutationResult: mutateResult,
    notes: account?.notes ?? '',
    outreachType: outreachTypeOption!,
    days: currentSchedule && currentSchedule.asDays(),
    lastContacted,
  }
  return (
    <FormModal open hideModal={hideModal} title="Edit: Account Details">
      <Form<FormShape>
        render={props => <DetailsForm editSection={editSection} highlightDNCFields={highlightDNCFields} {...props} />}
        onSubmit={onSubmit}
        initialValues={{
          ...initialValues,
        }}
      />
    </FormModal>
  )
}
